import { DownOutlined, UpOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Checkbox, Divider, Dropdown, Tag } from 'antd';
// eslint-disable-next-line no-use-before-define
import * as React from 'react';

import Avatar from '../Avatar';
import ProjectUserPicker, {
  availableProjectUserDropdownHeight,
  useProjectUserDropdownHeight
} from './ProjectUserPicker';
import { filterProjectSearchUsers, ProjectSearchUser } from './assignees';
import styles from './filters.module.css';
import { ProjectSearchFilters } from './tokens';

export type ProjectSearchAssigneesFilterProps = {
  compact?: boolean;
  currentUser: ProjectSearchUser | null;
  filters: ProjectSearchFilters;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  includeInvolves?: boolean;
  onChange: (filters: ProjectSearchFilters) => void;
  onOpenChange: (open: boolean) => void;
  onUserSearchQueryChange?: (query: string) => void;
  open: boolean;
  users: ProjectSearchUser[];
};

const ASSIGNEE_ROW_HEIGHT = 40;
const MAX_ASSIGNEE_LIST_HEIGHT = 240;
const DROPDOWN_FIXED_CONTENT_HEIGHT = 176;
const MAX_DROPDOWN_HEIGHT = 360;

const toggleToken = (tokens: string[], token: string) =>
  tokens.includes(token)
    ? tokens.filter(value => value !== token)
    : [...tokens, token];

export type SelectedAssigneeFilter = {
  dimension: 'assignee' | 'involves';
  label: string;
  token: string;
  user: ProjectSearchUser | null;
};

export const filterAssigneeUsers = filterProjectSearchUsers;

export const selectedAssigneeFilters = (
  filters: ProjectSearchFilters,
  users: ProjectSearchUser[],
  currentUser: ProjectSearchUser | null,
  includeInvolves = true
): SelectedAssigneeFilter[] => {
  const selectedUser = (token: string) =>
    token === 'me'
      ? currentUser
      : users.find(user => user.id === Number(token)) || null;

  return [
    ...filters.assignee
      .filter(token => token !== 'unassigned')
      .map(token => {
        const user = selectedUser(token);
        return {
          dimension: 'assignee' as const,
          label: user?.name || token,
          token,
          user
        };
      }),
    ...(includeInvolves
      ? filters.involves
          .filter(token => token !== 'me')
          .map(token => {
            const user = selectedUser(token);
            return {
              dimension: 'involves' as const,
              label: user?.name || token,
              token,
              user
            };
          })
      : [])
  ];
};

export const assigneeListHeight = (
  userCount: number,
  selectedFiltersHeight: number,
  dropdownHeight: number
) =>
  Math.min(
    MAX_ASSIGNEE_LIST_HEIGHT,
    userCount * ASSIGNEE_ROW_HEIGHT,
    Math.max(
      0,
      dropdownHeight - DROPDOWN_FIXED_CONTENT_HEIGHT - selectedFiltersHeight
    )
  );

export const availableAssigneeDropdownHeight = availableProjectUserDropdownHeight;

const AssigneesFilter: React.FC<ProjectSearchAssigneesFilterProps> = ({
  compact = false,
  currentUser,
  filters,
  getPopupContainer,
  includeInvolves = true,
  onChange,
  onOpenChange,
  onUserSearchQueryChange,
  open,
  users
}) => {
  const [selectedFiltersHeight, setSelectedFiltersHeight] = React.useState(0);
  const { dropdownHeight, dropdownPanelRef } = useProjectUserDropdownHeight(
    open,
    MAX_DROPDOWN_HEIGHT
  );
  const selectedFiltersRef = React.useRef<HTMLDivElement>(null);
  const filterCount =
    filters.assignee.length + (includeInvolves ? filters.involves.length : 0);
  const label = filterCount ? `Assignees (${filterCount})` : 'Assignees';

  const toggleAssignee = (token: string) =>
    onChange({ ...filters, assignee: toggleToken(filters.assignee, token) });
  const selectedFilters = selectedAssigneeFilters(
    filters,
    users,
    currentUser,
    includeInvolves
  );
  const selectedFiltersKey = selectedFilters
    .map(({ dimension, token }) => `${dimension}-${token}`)
    .join(',');

  React.useLayoutEffect(() => {
    const element = selectedFiltersRef.current;
    if (!element) {
      setSelectedFiltersHeight(0);
      return;
    }

    const updateHeight = () => {
      setSelectedFiltersHeight(element.getBoundingClientRect().height);
    };
    const observer = new ResizeObserver(updateHeight);
    updateHeight();
    observer.observe(element);

    return () => observer.disconnect();
  }, [selectedFiltersKey]);

  const listHeight = assigneeListHeight(
    users.length,
    selectedFiltersHeight,
    dropdownHeight
  );

  const removeSelectedFilter = (selectedFilter: SelectedAssigneeFilter) =>
    onChange({
      ...filters,
      [selectedFilter.dimension]: filters[selectedFilter.dimension].filter(
        token => token !== selectedFilter.token
      )
    });

  const overlay = (
    <div
      className={styles.dropdownPanel}
      ref={dropdownPanelRef}
      style={{ maxHeight: dropdownHeight }}
    >
      <ProjectUserPicker
        compact
        currentUser={currentUser}
        listHeight={listHeight}
        onToggle={toggleAssignee}
        onSearchQueryChange={onUserSearchQueryChange}
        rowHeight={ASSIGNEE_ROW_HEIGHT}
        selectedUserIds={filters.assignee}
        users={users}
      >
        {selectedFilters.length > 0 && (
          <div className={styles.selectedFilters} ref={selectedFiltersRef}>
            <div className={styles.selectedFilterTags}>
              {selectedFilters.map(selectedFilter => (
                <Tag
                  className={styles.selectedFilterTag}
                  closable
                  key={`${selectedFilter.dimension}-${selectedFilter.token}`}
                  onClose={event => {
                    event.preventDefault();
                    removeSelectedFilter(selectedFilter);
                  }}
                >
                  <Avatar
                    member={
                      selectedFilter.user || { name: selectedFilter.label }
                    }
                    size="small"
                    className={styles.selectedFilterAvatar}
                  />
                  {selectedFilter.label}
                </Tag>
              ))}
              <Button
                className={styles.clearAllButton}
                onClick={() =>
                  onChange({
                    ...filters,
                    assignee: [],
                    ...(includeInvolves ? { involves: [] } : {})
                  })
                }
                type="link"
              >
                Clear all
              </Button>
            </div>
          </div>
        )}
        {includeInvolves && (
          <>
            <label className={`${styles.assigneeRow} ${styles.compactUserRow}`}>
              <Checkbox
                checked={filters.involves.includes('me')}
                onChange={() =>
                  onChange({
                    ...filters,
                    involves: toggleToken(filters.involves, 'me')
                  })
                }
              />
              <Avatar
                member={currentUser || { name: 'Me' }}
                size="small"
                className={styles.avatar}
              />
              <span
                className={`${styles.assigneeName} ${styles.compactUserName}`}
              >
                Tasks created by or assigned to me
              </span>
            </label>
            <Divider className={`${styles.divider} ${styles.compactDivider}`} />
          </>
        )}
        <label className={`${styles.assigneeRow} ${styles.compactUserRow}`}>
          <Checkbox
            checked={filters.assignee.includes('unassigned')}
            onChange={() => toggleAssignee('unassigned')}
          />
          <span
            className={`${styles.unassignedAvatar} ${styles.compactUnassignedAvatar}`}
          >
            <UserOutlined />
          </span>
          <span className={`${styles.assigneeName} ${styles.compactUserName}`}>
            Unassigned
          </span>
        </label>
      </ProjectUserPicker>
    </div>
  );

  return (
    <Dropdown
      arrow={{ pointAtCenter: true }}
      getPopupContainer={
        getPopupContainer || (trigger => trigger.parentElement || document.body)
      }
      onOpenChange={onOpenChange}
      open={open}
      overlay={overlay}
      overlayClassName={`${styles.dropdownOverlay} ${
        compact ? styles.compactAssigneesDropdownOverlay : ''
      }`}
      placement={compact ? 'bottomCenter' : 'bottomLeft'}
      trigger={['click']}
    >
      <Button
        className={styles.filterPill}
        shape="round"
        size="small"
        type={open || filterCount ? 'primary' : 'default'}
      >
        {label}
        {open ? <UpOutlined /> : <DownOutlined />}
      </Button>
    </Dropdown>
  );
};

export default AssigneesFilter;
